"use client";

import { storyblokEditable, SbBlokData } from "@storyblok/react";
import Image from "next/image";
import {
  MARK_LINK,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_UL,
  NODE_IMAGE,
  render,
  NODE_HEADING,
  NODE_LI,
} from "storyblok-rich-text-react-renderer";
import { useState, useEffect } from "react";
import Cta from "./Cta";
import clsx from "clsx";

interface sbBlok extends SbBlokData {
  [key: string]: any;
}

interface TextComponentProps {
  blok: sbBlok;
}

const TextComponent = ({ blok }: TextComponentProps) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isFirstRender)
    return (
      <div className="grid-container py-7 lg:py-14">
        <div className="col-start-1 col-span-4 row-start-1 lg:col-start-3 lg:col-span-12 2xl:col-start-9 2xl:col-span-16">
          <div className="h-7 w-full bg-tm-c-gray-800 animate-pulse rounded-[10px]"></div>
        </div>
      </div>
    );

  return (
    <div
      className={clsx("grid-container", {
        "pt-7 lg:pt-14":
          !!blok.spacing_top?.length && blok.spacing_top[0].option === "large",
        "pt-14 lg:pt-[120px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "x_large",
        "pt-[120px] lg:pt-[180px]":
          !!blok.spacing_top?.length &&
          blok.spacing_top[0].option === "xx_large",
        "pb-7 lg:pb-14":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "large",
        "pb-14 lg:pb-[120px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "x_large",
        "pb-[120px] lg:pb-[180px]":
          !!blok.spacing_bottom?.length &&
          blok.spacing_bottom[0].option === "xx_large",
      })}
    >
      <div className="col-start-1 col-span-4 row-start-1 lg:col-start-3 lg:col-span-12 2xl:col-start-9 2xl:col-span-16">
        <div {...storyblokEditable(blok)}>
          <div style={{ textAlign: blok.title_alignment }}>
            {render(blok?.title, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children) => <p>{children}</p>,
                [NODE_HEADING]: (
                  children,
                  props: { level: 1 | 2 | 3 | 4 | 5 | 6 }
                ) => {
                  if (props?.level === 1) return <h1>{children}</h1>;

                  if (props?.level === 2) return <h2>{children}</h2>;

                  return <h3>{children}</h3>;
                },
              },
            })}
          </div>
          <div style={{ textAlign: blok.content_alignment }}>
            {render(blok?.content, {
              nodeResolvers: {
                [NODE_PARAGRAPH]: (children) => <p>{children}</p>,
                [NODE_UL]: (children) => (
                  <ul className="pl-16 list-disc">{children}</ul>
                ),
                [NODE_OL]: (children) => (
                  <ol className="pl-16 list-decimal">{children}</ol>
                ),
                [NODE_LI]: (children) => (
                  <li className="mb-6 last:mb-0">{children}</li>
                ),
                [NODE_IMAGE]: (_, props) => (
                  <div>
                    <Image
                      src={props.src as string}
                      alt={props.alt || "text-image"}
                      width={300}
                      height={150}
                      className="object-contain"
                    />
                  </div>
                ),
                [NODE_HEADING]: (
                  children,
                  props: { level: 1 | 2 | 3 | 4 | 5 | 6 }
                ) => {
                  if (props?.level === 1) return <h1>{children}</h1>;

                  if (props?.level === 2) return <h2>{children}</h2>;

                  return <h3>{children}</h3>;
                },
              },
              markResolvers: {
                [MARK_LINK]: (children, props) => {
                  return (
                    <a
                      href={props?.href}
                      target={props?.target}
                      style={{ color: "var(--color-tm-c-brand)" }}
                    >
                      {children}
                    </a>
                  );
                },
              },
            })}
          </div>
          {(blok.cta_button?.id || blok.cta_button?.url) && (
            <Cta
              name={
                blok.cta_button_name?.length > 0
                  ? blok.cta_button_name
                  : blok.cta_button?.story?.name
              }
              href={blok.cta_button?.url}
              fullSlug={blok.cta_button?.story?.full_slug}
              style={{ margin: "6rem auto 0" }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TextComponent;
